import { ValidatedForm } from "./ValidatedForm";
import { FieldRequirement } from "./FieldRequirement";
import { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLocale } from "../i18n";
export type DialogAction = {
  title: string;
  description: string;
  label: string;
  danger?: boolean;
  run: (reason: string, requestId: string) => Promise<void>;
};
export function ActionDialog({
  action,
  onClose,
  onSuccess,
}: {
  action: DialogAction;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}) {
  const { w } = useLocale();
  const ref = useRef<HTMLDialogElement>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [applied, setApplied] = useState(false);
  const committed = useRef(false);
  const pending = useRef<{reason:string; id:string} | null>(null);
  const running = useRef(false);
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  async function apply() {
    if (running.current || !reason.trim()) return;
    running.current = true;
    setBusy(true);
    setError("");
    try {
      if (!committed.current) {
        if (pending.current?.reason !== reason.trim()) pending.current = {reason:reason.trim(), id:crypto.randomUUID()};
        await action.run(reason.trim(), pending.current.id);
        committed.current = true;
        setApplied(true);
      }
      await onSuccess();
      onClose();
    } catch {
      setError(
        committed.current ? w("تم تنفيذ الإجراء، لكن تعذر تحديث العرض. أعد تحميل البيانات للمتابعة.", "The action succeeded, but the view could not refresh. Reload the data to continue.") : w(
          "لم يتم تأكيد تنفيذ الإجراء. حدّث البيانات قبل المحاولة مجدداً.",
          "The action could not be confirmed. Refresh the data before trying again.",
        ),
      );
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  return (
    <dialog
      ref={ref}
      className="action-dialog"
      aria-labelledby="dialog-title"
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current && !busy) onClose();
      }}
    >
      <div className="dialog-header">
        <h2 id="dialog-title">{action.title}</h2>
        <button
          className="icon-button"
          disabled={busy}
          onClick={onClose}
          aria-label={w("إغلاق", "Close")}
        >
          <XMarkIcon />
        </button>
      </div>
      <p className="muted dialog-description">{action.description}</p>
      <ValidatedForm onSubmit={event => { event.preventDefault(); void apply(); }}>
      <label htmlFor="reason"><span className="field-caption">{w("ملاحظات القرار", "Decision notes")}<FieldRequirement required={true} /></span></label>
      <textarea
        id="reason"
        autoFocus
        maxLength={1000}
        rows={4}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={busy || applied}
        placeholder={w(
          "دوّن سبب الإجراء لتوثيقه…",
          "Record the reason for this action…",
        )} required
      />
      {error && (
        <p role="alert" className="error-box">
          {error}
        </p>
      )}
      <div className="dialog-actions">
        <button type="button" className="secondary" onClick={onClose} disabled={busy}>
          {w("إلغاء", "Cancel")}
        </button>
        <button
          className={action.danger ? "danger" : "primary"}
          disabled={busy}
          type="submit"
        >
          {busy ? w("يرجى الانتظار…", "Please wait…") : applied ? w("تحديث البيانات", "Refresh data") : action.label}
        </button>
      </div>
      </ValidatedForm>
    </dialog>
  );
}
